import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar, Clock, MapPin, Plus, ChevronLeft, ChevronRight,
  BookOpen, FileText, Palmtree, Users, AlertTriangle, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = {
  course: { label: 'Cours', icon: BookOpen, color: '#3B82F6', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  exam: { label: 'Examen', icon: FileText, color: '#EF4444', bg: 'bg-red-50 text-red-700 border-red-200' },
  holiday: { label: 'Conge', icon: Palmtree, color: '#10B981', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  meeting: { label: 'Reunion', icon: Users, color: '#8B5CF6', bg: 'bg-purple-50 text-purple-700 border-purple-200' },
  deadline: { label: 'Echeance', icon: AlertTriangle, color: '#F59E0B', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
  other: { label: 'Autre', icon: Calendar, color: '#6B7280', bg: 'bg-gray-50 text-gray-700 border-gray-200' }
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

export const SchedulePage = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const isAdmin = !['student'].includes(user?.role);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schedule/events`);
      setEvents(res.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;

  const calendarDays = [];
  for (let i = 0; i < startOffset; i++) calendarDays.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) calendarDays.push(d);

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => {
      const start = e.start_date?.substring(0, 10);
      const end = e.end_date?.substring(0, 10) || start;
      return start <= dateStr && dateStr <= end;
    });
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  // Weekly schedule (recurring events)
  const weeklySchedule = DAYS.map((dayName, idx) => ({
    day: dayName,
    events: events.filter(e => e.is_recurring && e.day_of_week === idx)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
  }));

  // Upcoming events
  const todayStr = today.toISOString().substring(0, 10);
  const upcoming = events.filter(e => (e.start_date || '').substring(0, 10) >= todayStr)
    .sort((a, b) => a.start_date.localeCompare(b.start_date))
    .slice(0, 8);

  if (loading) return <LoadingSkel />;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="schedule-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Calendrier academique</h1>
          <p className="text-sm text-gray-500 mt-1">Emploi du temps et evenements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchEvents}><RefreshCw className="w-4 h-4 mr-2" />Actualiser</Button>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />Ajouter
            </Button>
          )}
        </div>
      </div>

      {/* Event type legend */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(EVENT_TYPES).map(([key, val]) => (
          <Badge key={key} variant="outline" className={`gap-1 text-xs ${val.bg}`}>
            <val.icon className="w-3 h-3" />{val.label}
          </Badge>
        ))}
      </div>

      <Tabs defaultValue="calendar">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendrier</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Emploi du temps</TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">A venir</TabsTrigger>
        </TabsList>

        {/* CALENDAR VIEW */}
        <TabsContent value="calendar" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-5 h-5" /></Button>
                <CardTitle className="text-lg">{MONTHS[month]} {year}</CardTitle>
                <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-5 h-5" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-md overflow-hidden">
                {DAYS.map(d => (
                  <div key={d} className="bg-gray-50 p-2 text-center text-xs font-semibold text-gray-600">{d}</div>
                ))}
                {calendarDays.map((day, i) => {
                  const dayEvents = getEventsForDay(day);
                  return (
                    <div
                      key={i}
                      className={`bg-white min-h-[80px] p-1 ${!day ? 'bg-gray-50' : ''} ${isToday(day) ? 'ring-2 ring-[var(--tenant-primary)] ring-inset' : ''}`}
                      data-testid={day ? `calendar-day-${day}` : undefined}
                    >
                      {day && (
                        <>
                          <span className={`text-xs font-medium ${isToday(day) ? 'text-[var(--tenant-primary)] font-bold' : 'text-gray-700'}`}>{day}</span>
                          <div className="space-y-0.5 mt-0.5">
                            {dayEvents.slice(0, 3).map(e => {
                              const type = EVENT_TYPES[e.event_type] || EVENT_TYPES.other;
                              return (
                                <div
                                  key={e.id}
                                  className="text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity"
                                  style={{ backgroundColor: type.color + '20', color: type.color, borderLeft: `2px solid ${type.color}` }}
                                  onClick={() => setSelectedEvent(e)}
                                  title={e.title}
                                >
                                  {e.title}
                                </div>
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <span className="text-[10px] text-gray-400">+{dayEvents.length - 3}</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WEEKLY SCHEDULE */}
        <TabsContent value="weekly" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {weeklySchedule.map(({ day, events: dayEvts }) => (
              <Card key={day} className={dayEvts.length > 0 ? '' : 'opacity-60'}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">{day}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dayEvts.length === 0 && <p className="text-xs text-gray-400 italic">Pas de cours</p>}
                  {dayEvts.map(e => {
                    const type = EVENT_TYPES[e.event_type] || EVENT_TYPES.other;
                    return (
                      <div key={e.id} className="p-2 rounded-md border cursor-pointer hover:shadow-sm transition-shadow"
                        style={{ borderLeftColor: type.color, borderLeftWidth: 3 }}
                        onClick={() => setSelectedEvent(e)}>
                        <p className="text-sm font-medium">{e.title}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                          {e.start_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{e.start_time} - {e.end_time}</span>}
                          {e.room && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.room}</span>}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* UPCOMING EVENTS */}
        <TabsContent value="upcoming" className="mt-4 space-y-3">
          {upcoming.length === 0 && (
            <Card><CardContent className="p-8 text-center text-gray-500">Aucun evenement a venir</CardContent></Card>
          )}
          {upcoming.map(e => {
            const type = EVENT_TYPES[e.event_type] || EVENT_TYPES.other;
            const TypeIcon = type.icon;
            return (
              <Card key={e.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setSelectedEvent(e)}
                data-testid={`upcoming-event-${e.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: type.color + '15' }}>
                    <TypeIcon className="w-5 h-5" style={{ color: type.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{e.title}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span>{new Date(e.start_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                      {e.start_time && <span>{e.start_time} - {e.end_time}</span>}
                      {e.room && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.room}</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-xs ${type.bg}`}>{type.label}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Create Event Dialog */}
      <CreateEventDialog open={showCreate} onOpenChange={setShowCreate} onSuccess={fetchEvents} />

      {/* Event Detail Dialog */}
      {selectedEvent && (
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{selectedEvent.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Badge variant="outline" className={EVENT_TYPES[selectedEvent.event_type]?.bg || ''}>
                {EVENT_TYPES[selectedEvent.event_type]?.label || selectedEvent.event_type}
              </Badge>
              {selectedEvent.description && <p className="text-sm text-gray-600">{selectedEvent.description}</p>}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" />
                  {new Date(selectedEvent.start_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
                {selectedEvent.start_time && (
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" />{selectedEvent.start_time} - {selectedEvent.end_time}</div>
                )}
                {selectedEvent.room && (
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" />{selectedEvent.room}</div>
                )}
                {selectedEvent.is_recurring && <Badge variant="outline" className="text-xs">Recurrent</Badge>}
              </div>
              <Button onClick={() => setSelectedEvent(null)} className="w-full" variant="outline">Fermer</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const CreateEventDialog = ({ open, onOpenChange, onSuccess }) => {
  const [form, setForm] = useState({
    title: '', event_type: 'course', start_date: '', end_date: '', start_time: '', end_time: '',
    day_of_week: '', is_recurring: false, room: '', description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, day_of_week: form.day_of_week !== '' ? parseInt(form.day_of_week) : null };
      await axios.post(`${API_URL}/api/schedule/events`, payload);
      toast.success('Evenement cree');
      onOpenChange(false);
      setForm({ title: '', event_type: 'course', start_date: '', end_date: '', start_time: '', end_time: '', day_of_week: '', is_recurring: false, room: '', description: '' });
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const upd = (f, v) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Nouvel evenement</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Titre</Label>
              <Input value={form.title} onChange={e => upd('title', e.target.value)} required data-testid="event-title" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.event_type} onValueChange={v => upd('event_type', v)}>
                <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EVENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Salle</Label>
              <Input value={form.room} onChange={e => upd('room', e.target.value)} placeholder="Salle A101" />
            </div>
            <div>
              <Label>Date debut</Label>
              <Input type="date" value={form.start_date} onChange={e => upd('start_date', e.target.value)} required />
            </div>
            <div>
              <Label>Date fin</Label>
              <Input type="date" value={form.end_date} onChange={e => upd('end_date', e.target.value)} />
            </div>
            <div>
              <Label>Heure debut</Label>
              <Input type="time" value={form.start_time} onChange={e => upd('start_time', e.target.value)} />
            </div>
            <div>
              <Label>Heure fin</Label>
              <Input type="time" value={form.end_time} onChange={e => upd('end_time', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => upd('description', e.target.value)} placeholder="Description optionnelle" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.is_recurring} onChange={e => upd('is_recurring', e.target.checked)} id="recurring" className="rounded" />
              <Label htmlFor="recurring" className="mb-0">Recurrent (hebdomadaire)</Label>
              {form.is_recurring && (
                <Select value={form.day_of_week.toString()} onValueChange={v => upd('day_of_week', v)}>
                  <SelectTrigger className="w-32"><SelectValue placeholder="Jour" /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d, i) => <SelectItem key={i} value={i.toString()}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={loading} data-testid="event-submit">{loading ? 'Creation...' : 'Creer'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LoadingSkel = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-64" />
    <div className="h-96 bg-gray-200 rounded-md" />
  </div>
);

export default SchedulePage;
